/**
 * Shared Quill modules for blog create/edit. Keep toolbar + handlers in one place.
 * Table handler must be a `function` (not arrow) so Quill can call it with the toolbar as `this`.
 * Link / blockquote: explicit handlers so link tooltips and toggles work with our merged toolbar handlers.
 */
import { Scope } from "parchment";

export const BLOG_Q_DEFAULT_MIN_HEIGHT = 320;

export function makeTableInsertHandler() {
  return function insertTable3x3() {
    this.quill.getModule("table")?.insertTable(3, 3);
  };
}

/**
 * Full link behavior: tooltips for selections; prompt fallback when the tooltip cannot open (e.g. some edge cases).
 */
export function makeBlogLinkHandler() {
  return function blogLinkHandler(value) {
    const quill = this.quill;
    const { tooltip } = quill.theme || {};
    if (!value) {
      quill.format("link", false, "user");
      return;
    }
    const range = quill.getSelection(true);
    if (range == null) return;
    if (range.length > 0) {
      let preview = quill.getText(range);
      if (/^\S+@\S+\.\S+$/.test(preview) && preview.indexOf("mailto:") !== 0) {
        preview = `mailto:${preview}`;
      }
      if (tooltip && typeof tooltip.edit === "function") {
        tooltip.edit("link", preview);
        return;
      }
      const u = window.prompt("Link URL", preview || "https://");
      if (u != null) quill.format("link", u || false, "user");
      return;
    }
    const fmt = quill.getFormat(range);
    if (fmt.link) {
      const u = window.prompt("Edit link URL", String(fmt.link));
      if (u != null) quill.format("link", u || false, "user");
      return;
    }
    const u = window.prompt("Link URL (applies to the next text you type)", "https://");
    if (u != null && String(u).trim() !== "") quill.format("link", String(u).trim(), "user");
  };
}

export function makeBlockquoteHandler() {
  return function blockquoteHandler(value) {
    const quill = this.quill;
    const range = quill.getSelection(true);
    if (range == null) return;
    quill.format("blockquote", value, "user");
  };
}

/**
 * Extends default clean: collapsed selection also clears blockquote / code-block and common block formats.
 */
export function makeBlogCleanHandler() {
  return function blogCleanHandler() {
    const quill = this.quill;
    const range = quill.getSelection(true);
    if (range == null) return;
    if (range.length > 0) {
      quill.removeFormat(range.index, range.length, "user");
      return;
    }
    const formats = quill.getFormat();
    Object.keys(formats).forEach((name) => {
      if (quill.scroll.query(name, Scope.INLINE) != null) {
        quill.format(name, false, "user");
      }
    });
    ["blockquote", "code-block", "list", "header"].forEach((k) => {
      if (formats[k] == null) return;
      if (k === "list") {
        quill.format("list", false, "user");
      } else {
        quill.format(k, false, "user");
      }
    });
  };
}

/**
 * @param {object} opts
 * @param {function} opts.imageHandler
 */
export function createBlogQuillModules({ imageHandler }) {
  return {
    table: true,
    keyboard: {
      bindings: {
        enterInTableCell: {
          key: 13,
          handler(range, context) {
            const inTableCell = Boolean(context?.format?.table);
            if (!inTableCell) return true;
            const nextFormats = { ...(context?.format ?? {}) };
            this.quill.insertText(range.index, "\n", nextFormats, "user");
            this.quill.setSelection(range.index + 1, 0, "silent");
            return false;
          }
        }
      }
    },
    toolbar: {
      container: [
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        ["bold", "italic", "underline", "strike", { script: "sub" }, { script: "super" }],
        ["blockquote", "code-block"],
        [{ color: [] }, { background: [] }],
        [{ list: "ordered" }, { list: "bullet" }, { list: "check" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ align: [] }, { direction: "rtl" }],
        ["link", "image", "table"],
        ["clean"]
      ],
      handlers: {
        image: imageHandler,
        table: makeTableInsertHandler(),
        link: makeBlogLinkHandler(),
        blockquote: makeBlockquoteHandler(),
        clean: makeBlogCleanHandler()
      }
    },
    clipboard: { matchVisual: false }
  };
}
